import React, { useEffect, useRef, useState } from "react";
import {
  Box, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  MenuItem, Modal, Radio, RadioGroup, Switch, TextField, Tooltip,
} from "@mui/material";
import Button from "../../../Subcomponents/Button";
import Close from "../../Icons/Close";
import { errorTost } from "../../../Services/Toast";
import cssClasses from "./Crm2Settings.module.css";
import own from "./NotificationsSection.module.css";

// CRM 2.0 notifications (M1-F8, design §7): the `events` list of Settings › CRM 2.0 › Notifications.
// Loading, saving, the Save bar and the field errors stay with the settings framework
// (Crm2Settings.jsx, Crm2CompanySettings → SectionForm); this draws only the events list:
// one row per wired event (the rows follow the code catalogue, none added or removed here),
// an On switch that needs the Go live permission, and an Edit pop-up with the texts and their
// blanks. The server checks every rule again on save (design §7.2) and its errors show here.
//
// From GET /crm2/settings/meta:
//   rule.item                    · the event row's fields (labels, options, max lengths)
//   options.notificationEvents   · [{ key, name, fields, namesPeople, audienceFromCall, scope }]
//   options.whatsappTemplates    · [{ value, templateId, label, name, language, contractVersion, text, header,
//                                   slots: [{ index, label }], sample }] (H-038: Tara's hub)
//   options.whatsappToolLive     · false → "WhatsApp tool not connected yet"

// Same look as Crm2Settings.jsx
const inputStyle = { fontSize: "1.4rem" };
const menuSx = { fontSize: "1.4rem" };
const chipSx = { fontSize: "1.2rem" };
const btnPrimary = { width: "fit-content", color: "#FFFFFF", backgroundColor: "#155EEF", border: "1px solid #155EEF", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };
const btnSecondary = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };

const EVENT_PEOPLE = "EVENT_PEOPLE";
const NONE = "NONE";
const SET_ON_ASK = "SET_ON_ASK";
const WHATSAPP = "WHATSAPP";
const TEXT_KEYS = ["staffTitle", "staffText", "customerSubject", "customerText"];
const OFF_TEXT = "It stops new messages and those still waiting.";
const GO_LIVE_TIP = "Needs the Go live permission";

// Label and hint when the server's field list gives none (it normally does).
const FALLBACK = {
  staffRoles: ["Who gets it", "Empty = no staff message"],
  staffChannels: ["Channels"],
  staffTitle: ["Title", "The bell line and the staff email subject"],
  staffText: ["Text", "The staff email text; the bell shows it under the title"],
  customerAudience: ["Who gets it"],
  customerChannels: ["Channels"],
  customerSubject: ["Email subject"],
  customerText: ["Email text"],
  whatsappTemplate: ["WhatsApp template", "Empty = no WhatsApp"],
};

// A blank is {{name}}; anything between double braces counts (as the server's notifyText.js).
const BLANK_RE = /\{\{\s*([^{}]*?)\s*\}\}/g;
function blanksIn(text) {
  const names = [];
  if (typeof text !== "string" || !text) return names;
  const re = new RegExp(BLANK_RE.source, "g");
  let m;
  while ((m = re.exec(text))) if (!names.includes(m[1])) names.push(m[1]);
  return names;
}

function messageText(err) {
  if (!err) return null;
  if (typeof err === "string") return err;
  return err.message || "Please fix this";
}

// What the catalogue says about one event. Without it (older server) nothing is guessed:
// blanks are not checked here and the server still checks them on save.
function eventInfoFor(options, row) {
  const all = options.notificationEvents;
  const info = Array.isArray(all) ? all.find((e) => e.key === row.key) : all?.[row.key];
  return {
    known: !!info,
    fields: info?.fields || [],
    namesPeople: info ? !!info.namesPeople : (row.staffRoles || []).includes(EVENT_PEOPLE),
    audienceFromCall: info ? !!info.audienceFromCall : row.customerAudience === SET_ON_ASK,
    // staff only (WhatsApp not sent, DC-240): no customer message
    staffOnly: !!info?.staffOnly,
    // a project event (health check) has no booking, so no customer message
    project: info?.scope === "PROJECT",
  };
}

// Labels of the picked values, in the order of the options.
function labelsOf(rule, values) {
  const list = rule?.options || [];
  const picked = values || [];
  return [
    ...list.filter((o) => picked.includes(o.value)).map((o) => o.label),
    ...picked.filter((v) => !list.some((o) => o.value === v)).map(String),
  ];
}

function staffLine(row, item) {
  const roles = labelsOf(item.staffRoles, row.staffRoles);
  const channels = labelsOf(item.staffChannels, row.staffChannels);
  if (!roles.length && !channels.length) return "Staff: none";
  return `Staff: ${roles.join(", ") || "nobody picked"} · ${channels.join(", ") || "no channel"}`;
}

function customerLine(row, item) {
  const audience = row.customerAudience;
  if (!audience || audience === NONE) return "Customers: none";
  const who = item.customerAudience?.options?.find((o) => o.value === audience)?.label || audience;
  const channels = labelsOf(item.customerChannels, row.customerChannels);
  return `Customers: ${who} · ${channels.join(", ") || "no channel"}`;
}

// The save rules of design §7.2, checked as the user types (the server checks them again).
// Like the server, only rows that are On are checked: an Off row is checked when it is switched On.
function eventProblems(row, info, options) {
  const p = {};
  if (!row.isActive) return p;
  if (info.known) {
    const allowed = new Set(info.fields);
    TEXT_KEYS.forEach((k) => {
      const bad = blanksIn(row[k]).filter((n) => !allowed.has(n));
      if (bad.length) p[k] = bad.map((n) => `{{${n}}} is not a blank this event can fill`).join(" · ");
    });
    // WhatsApp (H-038): the template as the tool lists it now, a value the event can fill for every blank
    const m = row.whatsappTemplate;
    const { listed, changed } = templateState(options, m);
    if (m && listed) {
      const notFillable = [...new Set((m.slots || []).map((s) => s.field).filter((f) => f && !allowed.has(f)))];
      const unmapped = (listed.slots || []).filter((x) => !(m.slots || []).some((s) => s.index === x.index && s.field)).map((x) => blankName(x.index));
      if (listed.header?.required) p.whatsappTemplate = "This template needs a header image or document, which this event can't send yet";
      else if (changed) p.whatsappTemplate = "This template changed at the tool: open it again and check the blanks";
      else if (notFillable.length) p.whatsappTemplate = `${notFillable.map(blankName).join(", ")} ${notFillable.length === 1 ? "is not a value" : "are not values"} this event can fill`;
      else if (unmapped.length) p.whatsappTemplate = `Pick a value for ${unmapped.length === 1 ? "blank" : "blanks"} ${unmapped.join(", ")}`;
    }
  }
  // only while the tool is connected; until then the WhatsApp rows wait (TOOL_NOT_LIVE)
  if (!p.whatsappTemplate && options.whatsappToolLive && (row.customerChannels || []).includes(WHATSAPP) && !row.whatsappTemplate) {
    p.whatsappTemplate = "Pick a WhatsApp template: until then the WhatsApp messages of this event are skipped";
  }
  const roles = row.staffRoles || [];
  const channels = row.staffChannels || [];
  if (channels.length && !roles.length) p.staffRoles = "Pick who gets it";
  if (roles.length && !channels.length) p.staffChannels = "Pick a channel";
  return p;
}

// ---- small parts ------------------------------------------------------------------------
function Block({ label, hint, problem, children }) {
  return (
    <div className={cssClasses.field} style={{ width: "100%" }}>
      <span className={cssClasses.label}>{label}</span>
      {children}
      {hint && <p className={cssClasses.hint}>{hint}</p>}
      {problem && <p className={cssClasses.error}>{messageText(problem)}</p>}
    </div>
  );
}

function Checks({ list, value, onChange, disabled }) {
  const current = value || [];
  if (!list.length) return <p className={cssClasses.hint}>Nothing to choose from yet.</p>;
  return (
    <div className={cssClasses.checkWrap}>
      {list.map((o) => (
        <FormControlLabel key={String(o.value)} disabled={disabled}
          control={<Checkbox size="small" checked={current.includes(o.value)}
            onChange={(e) => onChange(e.target.checked ? [...current, o.value] : current.filter((v) => v !== o.value))} />}
          label={<span className={cssClasses.radioText}>{o.label}</span>} />
      ))}
    </div>
  );
}

// A text box with its "Blanks" line: a click puts {{field}} where the cursor is in this box
// (at the end when the box was never clicked) and keeps typing there.
function TextWithBlanks({ rule, label, hint, value, onChange, fields, readOnly, problem, multiline }) {
  const input = useRef(null);
  const caret = useRef(null);
  useEffect(() => {
    const el = input.current;
    if (caret.current === null || !el) return;
    const at = caret.current;
    caret.current = null;
    el.focus();
    el.setSelectionRange(at, at);
  }, [value]);

  function insert(name) {
    const el = input.current;
    const text = value || "";
    const blank = `{{${name}}}`;
    const start = typeof el?.selectionStart === "number" ? el.selectionStart : text.length;
    const end = typeof el?.selectionEnd === "number" ? el.selectionEnd : start;
    const next = text.slice(0, start) + blank + text.slice(end);
    if (rule?.maxLength && next.length > rule.maxLength) {
      errorTost(`Keep it under ${rule.maxLength} characters`);
      return;
    }
    caret.current = start + blank.length;
    onChange(next);
  }

  return (
    <Block label={label} problem={problem}>
      <TextField size="small" value={value ?? ""} disabled={readOnly} error={!!problem} inputRef={input}
        multiline={multiline} minRows={multiline ? 4 : undefined} onChange={(e) => onChange(e.target.value)}
        inputProps={{ style: inputStyle, maxLength: rule?.maxLength, "aria-label": label }} sx={{ width: "100%" }} />
      {fields.length > 0 && (
        <div className={own.blanks}>
          <span className={own.blanksLabel}>Blanks</span>
          {fields.map((f) => (
            <Chip key={f} size="small" label={f} sx={chipSx}
              // keep the cursor in the box while the chip is pressed
              onMouseDown={(e) => e.preventDefault()}
              onClick={readOnly ? undefined : () => insert(f)} />
          ))}
        </div>
      )}
      {hint && <p className={cssClasses.hint}>{hint}</p>}
    </Block>
  );
}

// The WhatsApp template of a row (H-038, Tara's Communication Hub): a template from the tool's approved list and,
// for each of its blanks, the value that fills it:
//   { templateId, name, language, contractVersion, header, slots: [{ index, label, field }] } | null
// A blank keeps its value when the template is picked again only where the blank is the same (same number, same
// label); otherwise it starts empty or with the value named like its label.
function freshMapping(t, fields, prev) {
  const before = prev && prev.templateId === t.templateId ? prev.slots || [] : [];
  return {
    templateId: t.templateId,
    name: t.name,
    language: t.language,
    contractVersion: t.contractVersion,
    header: t.header || null,
    slots: (t.slots || []).map((s) => {
      const kept = before.find((x) => x.index === s.index && x.label === s.label)?.field;
      return { index: s.index, label: s.label, field: kept || (fields.includes(s.label) ? s.label : "") };
    }),
  };
}

// The listed template of a mapping, and whether the tool has changed it since it was mapped.
function templateState(options, mapping) {
  if (!mapping) return { listed: null, changed: false, gone: false };
  const listed = (options.whatsappTemplates || []).find((t) => t.templateId === mapping.templateId) || null;
  return { listed, changed: !!listed && listed.contractVersion !== mapping.contractVersion, gone: !listed };
}

const blankName = (index) => `{{${index}}}`;

// Pick from the tool's approved templates, then a value for each blank. Not connected → the note, and the
// pick is open only when the list has sample templates (test stub).
function TemplatePick({ rule, label, hint, value, onChange, readOnly, options, problem, fields }) {
  const list = options.whatsappTemplates || [];
  const live = !!options.whatsappToolLive;
  const usable = live || list.some((t) => t.sample);
  const { listed, changed, gone } = templateState(options, value);
  const shown = gone ? [...list, { value: String(value.templateId), label: `${value.name} (${value.language})`, gone: true }] : list;
  // every blank of the template as listed now (or as mapped while the tool has it changed or gone)
  const blanks = (listed && !changed ? listed.slots : value?.slots || []).map((s) => ({
    index: s.index,
    label: s.label,
    field: (value?.slots || []).find((x) => x.index === s.index)?.field || "",
  }));

  function pick(v) {
    if (v === "__null") return onChange(null);
    const t = list.find((x) => x.value === v);
    if (t) onChange(freshMapping(t, fields, value));
  }
  function setField(index, field) {
    const slots = blanks.map((b) => ({ index: b.index, label: b.label, field: b.index === index ? field : b.field }));
    onChange({ ...value, slots });
  }

  return (
    <Block label={label} problem={problem}>
      <TextField select size="small" value={value ? String(value.templateId) : "__null"} disabled={readOnly || !usable} error={!!problem}
        onChange={(e) => pick(e.target.value)}
        SelectProps={{ style: inputStyle }} inputProps={{ "aria-label": label }} sx={{ width: "48rem", maxWidth: "100%" }}>
        <MenuItem value="__null" sx={menuSx}>{rule?.emptyLabel || "No WhatsApp"}</MenuItem>
        {shown.map((t) => (
          <MenuItem key={t.value} value={t.value} sx={menuSx}>
            {t.label}
            {t.sample && <span className={cssClasses.chipMuted} style={{ marginLeft: ".8rem" }}>Sample</span>}
            {t.gone && <span className={cssClasses.hint} style={{ margin: "0 0 0 .8rem" }}>not in the tool's list now</span>}
          </MenuItem>
        ))}
      </TextField>
      {!live && <p className={cssClasses.hint}>WhatsApp tool not connected yet. WhatsApp messages wait until it is.</p>}
      {changed && (
        <div className={own.blanks}>
          <p className={cssClasses.error} style={{ margin: 0 }}>This template changed at the tool. Check every blank and save.</p>
          {!readOnly && <button type="button" className={cssClasses.pillEdit} onClick={() => onChange(freshMapping(listed, fields, value))}>Check the blanks again</button>}
        </div>
      )}
      {value && (
        <>
          {listed?.text && !changed && <p className={own.preview}>{listed.text}</p>}
          {blanks.length === 0 ? (
            <p className={cssClasses.hint}>This template has no blanks.</p>
          ) : (
            <div className={cssClasses.tableCard}>
              <table className={cssClasses.table}>
                <thead>
                  <tr>
                    <th>Blank in the template</th>
                    <th>Our value</th>
                  </tr>
                </thead>
                <tbody>
                  {blanks.map((b) => (
                    <tr key={b.index}>
                      <td>
                        <b>{blankName(b.index)}</b>
                        {b.label && b.label !== String(b.index) && <span className={own.slotLabel}>{b.label}</span>}
                      </td>
                      <td>
                        <TextField select size="small" value={b.field} disabled={readOnly || changed} onChange={(e) => setField(b.index, e.target.value)}
                          SelectProps={{ style: inputStyle, displayEmpty: true }} inputProps={{ "aria-label": `Value for ${blankName(b.index)}` }} sx={{ width: "24rem", maxWidth: "100%" }}>
                          <MenuItem value="" sx={menuSx}><span className={cssClasses.hint} style={{ margin: 0 }}>Pick a value</span></MenuItem>
                          {[...fields, ...(b.field && !fields.includes(b.field) ? [b.field] : [])].map((f) => (
                            <MenuItem key={f} value={f} sx={menuSx}>{f}</MenuItem>
                          ))}
                        </TextField>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {hint && <p className={cssClasses.hint}>{hint}</p>}
    </Block>
  );
}

function ConfirmDialog({ open, title, text, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle sx={{ fontSize: "1.6rem", fontFamily: "Inter-Medium" }}>{title}</DialogTitle>
      <DialogContent><p className={cssClasses.headSub}>{text}</p></DialogContent>
      <DialogActions sx={{ padding: "1.6rem" }}>
        <Button style={btnSecondary} btnName="Cancel" clicked={onCancel} />
        <Button style={btnPrimary} btnName="Confirm" clicked={onConfirm} />
      </DialogActions>
    </Dialog>
  );
}

// ---- the Edit pop-up --------------------------------------------------------------------
function EventModal({ row, item, info, options, readOnly, serverErrors, onCancel, onSave }) {
  const [draft, setDraft] = useState(row);
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const label = (k) => item[k]?.label || FALLBACK[k]?.[0] || k;
  const hint = (k) => item[k]?.hint || FALLBACK[k]?.[1];
  // the server's errors for this row, then what is still wrong as the user edits
  const problems = { ...serverErrors, ...eventProblems(draft, info, options) };
  const roles = (item.staffRoles?.options || []).filter((o) => o.value !== EVENT_PEOPLE || info.namesPeople || (draft.staffRoles || []).includes(EVENT_PEOPLE));
  const audiences = (item.customerAudience?.options || []).filter((o) => o.value !== SET_ON_ASK);
  const setOnAsk = item.customerAudience?.options?.find((o) => o.value === SET_ON_ASK)?.label || "Set on each ask";
  const text = (k, multiline) => (
    <TextWithBlanks rule={item[k]} label={label(k)} hint={hint(k)} value={draft[k]} onChange={set(k)}
      fields={info.fields} readOnly={readOnly} problem={problems[k]} multiline={multiline} />
  );

  return (
    <Modal open onClose={(_, reason) => reason !== "backdropClick" && onCancel()} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
      <Box className={`${cssClasses.modalWrapper} ${cssClasses.modalWide}`}>
        <div className={cssClasses.modalHeader}>
          <span>{readOnly ? draft.name : `Edit ${draft.name}`}</span>
          <span className={cssClasses.modalClose} onClick={onCancel} tabIndex={0} role="button" aria-label="Close">
            <Close width="1.2rem" height="1.2rem" />
          </span>
        </div>
        <div className={cssClasses.modalContent}>
          {problems._ && <p className={cssClasses.error}>{messageText(problems._)}</p>}
          <div className={cssClasses.card}>
            <p className={cssClasses.cardTitle}>Staff</p>
            <Block label={label("staffRoles")} hint={hint("staffRoles")} problem={problems.staffRoles}>
              <Checks list={roles} value={draft.staffRoles} onChange={set("staffRoles")} disabled={readOnly} />
            </Block>
            <Block label={label("staffChannels")} hint={hint("staffChannels")} problem={problems.staffChannels}>
              <Checks list={item.staffChannels?.options || []} value={draft.staffChannels} onChange={set("staffChannels")} disabled={readOnly} />
            </Block>
            {text("staffTitle")}
            {text("staffText", true)}
          </div>
          {!info.project && !info.staffOnly && <div className={cssClasses.card}>
            <p className={cssClasses.cardTitle}>Customers</p>
            <Block label={label("customerAudience")} hint={info.audienceFromCall ? "Each ask picks who gets it." : hint("customerAudience")} problem={problems.customerAudience}>
              {info.audienceFromCall ? (
                <span className={cssClasses.radioText}>{setOnAsk}</span>
              ) : (
                <RadioGroup row value={draft.customerAudience ?? NONE} onChange={(e) => set("customerAudience")(e.target.value)}>
                  {audiences.map((o) => (
                    <FormControlLabel key={o.value} value={o.value} disabled={readOnly}
                      control={<Radio size="small" />} label={<span className={cssClasses.radioText}>{o.label}</span>} />
                  ))}
                </RadioGroup>
              )}
            </Block>
            <Block label={label("customerChannels")} hint={hint("customerChannels")} problem={problems.customerChannels}>
              <Checks list={item.customerChannels?.options || []} value={draft.customerChannels} onChange={set("customerChannels")} disabled={readOnly} />
            </Block>
            {text("customerSubject")}
            {text("customerText", true)}
            <TemplatePick rule={item.whatsappTemplate} label={label("whatsappTemplate")} hint={hint("whatsappTemplate")} value={draft.whatsappTemplate}
              onChange={set("whatsappTemplate")} readOnly={readOnly} options={options} problem={problems.whatsappTemplate} fields={info.fields} />
          </div>}
        </div>
        <div className={cssClasses.modalFooter}>
          {readOnly ? (
            <Button style={btnSecondary} btnName="Close" clicked={onCancel} />
          ) : (
            <>
              <Button style={btnSecondary} btnName="Cancel" clicked={onCancel} />
              <Button style={btnPrimary} btnName="Save" clicked={() => onSave(draft)} />
            </>
          )}
        </div>
      </Box>
    </Modal>
  );
}

// ---- the events list ----------------------------------------------------------------------
// Props as SectionForm gives a field (rule, value, onChange, disabled, options, error), plus
// canGoLive: "Go live (switch a message Live or Off)" under CRM 2.0 › Settings › Notifications.
export function NotificationEvents({ rule, value, onChange, disabled, options, error, canGoLive }) {
  const rows = value || [];
  const item = rule.item || {};
  const [editing, setEditing] = useState(null); // { index, row }
  const [confirm, setConfirm] = useState(null); // { index, name }: waiting for the switch-off confirm
  // Rows changed since the server's errors came back: their old errors are hidden
  // (what is still wrong shows from the checks above; the server checks again on save).
  const [touched, setTouched] = useState([]);
  useEffect(() => setTouched([]), [error]);

  const serverRows = (error && typeof error === "object" && error.rows) || {};
  const listError = typeof error === "string" ? error : error?.message;
  const serverFor = (i) => (touched.includes(i) ? {} : serverRows[i] || {});
  const label = (k) => item[k]?.label || FALLBACK[k]?.[0] || k;
  const onCount = rows.filter((r) => r.isActive).length;

  function setRow(i, next) {
    onChange(rows.map((r, j) => (j === i ? next : r)));
    setTouched((t) => (t.includes(i) ? t : [...t, i]));
  }

  return (
    <div className={cssClasses.listWrap}>
      <div className={cssClasses.listHead}>
        <span className={cssClasses.label}>{rows.length} {rows.length === 1 ? "message" : "messages"} · {onCount} on</span>
      </div>
      {listError && <p className={cssClasses.error}>{listError}</p>}
      {rows.length === 0 ? (
        <div className={cssClasses.empty}>{rule.emptyText || "No messages yet."}</div>
      ) : (
        <div className={cssClasses.tableCard}>
          <table className={cssClasses.table}>
            <thead>
              <tr>
                <th>Message</th>
                <th>On</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const problems = { ...serverFor(i), ...eventProblems(row, eventInfoFor(options, row), options) };
                const lines = Object.entries(problems).map(([k, m]) => (k === "_" ? messageText(m) : `${label(k)}: ${messageText(m)}`));
                const name = row.name || row.key;
                return (
                  <tr key={row.key || i} className={lines.length ? cssClasses.rowError : ""}>
                    <td>
                      <b>{name}</b>
                      <div className={own.lines}>
                        <span>{staffLine(row, item)}</span>
                        {!eventInfoFor(options, row).project && !eventInfoFor(options, row).staffOnly && <span>{customerLine(row, item)}</span>}
                        {templateState(options, row.whatsappTemplate).changed && <span><span className={cssClasses.chipMuted}>Check template</span></span>}
                      </div>
                      {lines.map((l, n) => <p key={n} className={cssClasses.error}>{l}</p>)}
                    </td>
                    <td style={{ width: "1%" }}>
                      <Tooltip title={canGoLive ? "" : <span style={{ fontSize: "1.2rem" }}>{GO_LIVE_TIP}</span>}>
                        <span>
                          <Switch size="small" checked={!!row.isActive} disabled={disabled || !canGoLive} inputProps={{ "aria-label": `On: ${name}` }}
                            onChange={(e) => {
                              if (e.target.checked) setRow(i, { ...row, isActive: true });
                              else setConfirm({ index: i, name });
                            }} />
                        </span>
                      </Tooltip>
                    </td>
                    <td style={{ width: "1%", whiteSpace: "nowrap", textAlign: "right" }}>
                      <button type="button" className={cssClasses.pillEdit} onClick={() => setEditing({ index: i, row })}>
                        {disabled ? "View" : "Edit"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <EventModal key={editing.index} row={editing.row} item={item} info={eventInfoFor(options, editing.row)} options={options}
          readOnly={disabled} serverErrors={serverFor(editing.index)}
          onCancel={() => setEditing(null)}
          onSave={(next) => {
            setRow(editing.index, next);
            setEditing(null);
          }} />
      )}
      <ConfirmDialog open={!!confirm} title={`Switch off ${confirm?.name}?`}
        text={rule.offConfirmText ? rule.offConfirmText.replace("{name}", confirm?.name || "") : OFF_TEXT}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setRow(confirm.index, { ...rows[confirm.index], isActive: false });
          setConfirm(null);
        }} />
    </div>
  );
}
